import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { AppState } from '../../store/state';
import { AppDispatch } from '../../store/store';
import { Submission } from '../../store/submission/types';
import {
  selectBidState,
  selectInfo,
  selectKeywordState,
  selectSubmissionState,
  selectTrackRoles,
} from '../../store/selectors';
import { connect } from 'react-redux';
import Loading from '../../components/Loading/Loading';
import { ChairInfo, Info, InfoDeleteDTO, InfoGetDTO, TableFriendlyName } from '../../store/info/types';
import { getSubmissionRow } from '../../helpers/table';
import MyGridRedux from '../../components/MyGridRedux/MyGridRedux';
import { useTranslation } from 'react-i18next';
import TableHeader from '../../components/TableHeader/TableHeader';
import { Role } from '../../store/conference/types';
import infoSlice from '../../store/info/slice';
import { getRouteByName } from '../../router/routes';
import history from '../../store/history';
import { fillRoutePath } from '../../helpers/path';
import { Bid, BidOption, ReviewerTableRow } from '../../store/bid/types';
import { getReviewer } from '../../helpers/users';
import useFetchTableData from '../../store/table/useFetchTableData';

interface Props {
  submissionsById: { [key: string]: Submission };
  loading: boolean;
  keywordById: { [key: number]: Keyword };
  info: Info;
  roleById: { [key: string]: Role };
  getInfoAction: (data: InfoGetDTO) => void;
  bidOptionsById: { [key: number]: BidOption };
  deleteInfoAction: (data: InfoDeleteDTO) => void;
}

const ReviewerBidsPage = ({
  submissionsById,
  loading,
  keywordById,
  info,
  roleById,
  getInfoAction,
  bidOptionsById,
  deleteInfoAction,
}: Props) => {
  const { t, i18n } = useTranslation();
  const params: any = useParams();
  const id = parseInt(params.id);

  const friendlyName: TableFriendlyName = 'reviewer_bids';
  const queryParams = { person_role_id: id };

  const [fetchData, fetchCustomColumnsData] = useFetchTableData(friendlyName, queryParams);

  useEffect(() => {
    if (Object.keys(info['users'].byId).length === 0) {
      getInfoAction({ friendlyName: 'users' });
    }
    return () => {
      deleteInfoAction({ friendlyName });
    };
  }, []);

  const chairInfo = info as ChairInfo;
  const reviewer: ReviewerTableRow | undefined = getReviewer(id, chairInfo.users);

  if (loading) {
    return <Loading />;
  } else if (!reviewer) {
    return <div>Reviewer #{id} not found</div>;
  }

  /* Generate data for table */
  const inputData: unknown[] = [];
  const bids = Object.values(info[friendlyName].byId) as Bid[];
  bids.forEach((bid) => {
    const newRegister = {
      ...getSubmissionRow(submissionsById[bid.submission_id], true, keywordById, t),
      bid: bid.bid_option_id ? bidOptionsById[bid.bid_option_id].name : undefined,
      conflict: bid.conflict,
    };

    inputData.push(newRegister);
  });

  const parentRoute = getRouteByName('RouteAllReviewersBids');

  return (
    <div className="flex flex-col h-full">
      <TableHeader
        onBack={() => history.push(fillRoutePath(parentRoute.path))}
        onMove={(rowId) => {
          getInfoAction({ friendlyName, queryParams: { ...queryParams, person_role_id: rowId } });
          history.replace(
            fillRoutePath(getRouteByName('RouteReviewerBids').path, {
              id: rowId.toString(),
            }),
          );
        }}
        type={'App\\Models\\Person'}
        row={reviewer}
        keywordById={keywordById}
        roleById={roleById}
      />
      <div className="mt-8" />
      <MyGridRedux friendlyName={friendlyName} inputData={inputData as Record<string, unknown>[]} />
    </div>
  );
};

const mapStateToProps = (state: AppState) => ({
  submissionsById: selectSubmissionState(state).submissionsById,
  loading: selectSubmissionState(state).loading || selectInfo(state).loading,
  keywordById: selectKeywordState(state).keywordById,
  info: state.info.info,
  roleById: selectTrackRoles(state),
  bidOptionsById: selectBidState(state).bidOptions.byId,
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  getInfoAction: (data: InfoGetDTO) => dispatch(infoSlice.actions.GET(data)),
  deleteInfoAction: (data: InfoDeleteDTO) => dispatch(infoSlice.actions.DELETE(data)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ReviewerBidsPage);
